import type { Hint, KeyStat } from './hints.js';
import type { RaceConfig, TypingSummary } from './typing.js';

export type RacePhase = 'lobby' | 'countdown' | 'racing' | 'results';

export interface PlayerInfo {
  id: string;
  name: string;
  vehicle: string;
  colour: string;
  isBot: boolean;
  isHost: boolean;
  ready: boolean;
  /** Team index (0-based) when the room is in team mode, else null. */
  team: number | null;
}

/** The display-relevant part of a player's typing state, for reconnects. */
export interface ResumeState {
  sentenceIndex: number;
  cursor: number;
  committedChars: number;
  bonusChars: number;
  errorBuffer: string;
  streak: number;
  streaksCompleted: number;
  stallUntil: number;
  correctCount: number;
  errorCount: number;
  backspaceCount: number;
  startedAt: number | null;
  finishedAt: number | null;
  place: number | null;
}

export interface RoomInfo {
  /** Short code, e.g. "BLUE" for private rooms; "PUBLIC" for the open room. */
  code: string;
  isPublic: boolean;
}

export const TEAM_NAMES = ['Red', 'Blue', 'Green'] as const;
export const TEAM_COLOURS = ['#e53935', '#1e88e5', '#43a047'] as const;

export interface TeamResult {
  team: number;
  name: string;
  colour: string;
  /** Placing points of every member, bots included, so bot-filled teams are not penalised. */
  score: number;
  members: string[];
  place: number;
}

export interface PlayerProgress {
  id: string;
  progress: number;
  wpm: number;
  accuracy: number;
  sentenceIndex: number;
  cursor: number;
  finished: boolean;
  place: number | null;
}

export interface RaceResult {
  id: string;
  name: string;
  vehicle: string;
  colour: string;
  isBot: boolean;
  place: number;
  summary: TypingSummary;
  points: number;
  bestAccuracy: boolean;
  hints: Hint[];
  keyStats: KeyStat[];
}

// ------------------------------------------------------------ accounts & stats
export interface RaceHistoryRow {
  playedAt: string;
  wpm: number;
  accuracy: number;
  place: number;
  players: number;
  mode: 'online' | 'practice';
  points: number;
}
export interface LessonHistoryRow {
  playedAt: string;
  lessonId: string;
  wpm: number;
  accuracy: number;
  stars: number;
}
export interface RecurringHint {
  id: string;
  title: string;
  count: number;
  /** Races considered. */
  races: number;
}
export interface UserStats {
  name: string;
  races: RaceHistoryRow[];
  lessons: LessonHistoryRow[];
  /** Aggregated over recent races, worst first. */
  keyStats: KeyStat[];
  recurringHints: RecurringHint[];
  summary: {
    races: number;
    lessons: number;
    avgWpm: number;
    bestWpm: number;
    avgAccuracy: number;
    /** Averages over the most recent 5 races and the 5 before, for trend arrows. */
    recentWpm: number;
    earlierWpm: number;
    recentAccuracy: number;
    earlierAccuracy: number;
  };
}

export interface LeagueRow {
  name: string;
  races: number;
  wins: number;
  podiums: number;
  points: number;
  bestWpm: number;
  avgWpm: number;
  avgAccuracy: number;
  lastPlayed: string;
}

export interface RecentRace {
  id: number;
  playedAt: string;
  winner: string;
  players: number;
  bestWpm: number;
}

export interface RoomOptions extends RaceConfig {
  fillWithBots: boolean;
  botWpm: number;
  /** Sentence pack id: 'motivation' or a subject id. */
  pack: string;
  /** 0 = individual race, 2 or 3 = number of teams. */
  teams: number;
}

export type ClientMessage =
  | { type: 'join'; name: string; vehicle: string; colour: string; token?: string; room?: string; session?: string }
  | { type: 'createRoom'; name: string; vehicle: string; colour: string; token?: string; session?: string }
  | { type: 'setTeam'; team: number }
  | { type: 'setVehicle'; vehicle: string; colour: string }
  | { type: 'setOptions'; options: Partial<RoomOptions> }
  | { type: 'ready'; ready: boolean }
  | { type: 'start' }
  | { type: 'key'; key: string; t: number }
  | { type: 'leave' }
  | { type: 'backToLobby' }
  | { type: 'requestLeague' }
  | { type: 'requestLobby' }
  | { type: 'ping'; t: number };

export type ServerMessage =
  | { type: 'welcome'; id: string; serverTime: number }
  | { type: 'lobby'; players: PlayerInfo[]; options: RoomOptions; phase: RacePhase; room: RoomInfo }
  | { type: 'countdown'; seconds: number; sentences: string[]; startAt: number; tip: string }
  | { type: 'go'; startAt: number }
  | {
      /** Sent to a player who reconnected during a race: everything needed to carry on. */
      type: 'resume';
      phase: RacePhase;
      sentences: string[];
      startAt: number;
      state: ResumeState;
    }
  | { type: 'progress'; players: PlayerProgress[]; serverTime: number }
  | { type: 'playerFinished'; id: string; place: number }
  | { type: 'results'; results: RaceResult[]; teams: TeamResult[] | null; sentences: string[] }
  | { type: 'league'; rows: LeagueRow[]; recent: RecentRace[] }
  | { type: 'error'; message: string }
  | { type: 'pong'; t: number; serverTime: number };

export const MAX_PLAYERS = 6;
export const PUBLIC_ROOM = 'PUBLIC';
/** Unambiguous alphabet for room codes (no 0/O, 1/I). */
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
export function makeRoomCode(rng: () => number = Math.random): string {
  let c = '';
  for (let i = 0; i < 4; i++) c += CODE_ALPHABET[Math.floor(rng() * CODE_ALPHABET.length)];
  return c;
}
export function normaliseRoomCode(raw: string): string | null {
  const c = raw.trim().toUpperCase().replace(/[^A-Z0-9]/g, '');
  return /^[A-Z2-9]{4}$/.test(c) ? c : null;
}
export const NAME_MIN = 2;
export const NAME_MAX = 16;

export function sanitiseName(raw: string): string | null {
  const name = raw.trim().replace(/\s+/g, ' ');
  if (name.length < NAME_MIN || name.length > NAME_MAX) return null;
  if (!/^[\p{L}\p{N} _.'-]+$/u.test(name)) return null;
  return name;
}
